import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCountUp } from '@/hooks/useCountUp';

import evexLogo from '@/assets/companies/evex-logo.png';
import focusLogo from '@/assets/companies/focus-logo.png';
import whitelightLogo from '@/assets/companies/whitelight-logo.png';
import scurveLogo from '@/assets/companies/scurve-logo.png';

import evexLogoMobile from '@/assets/companies/evex-logo-mobile.png';
import focusLogoMobile from '@/assets/companies/focus-logo-mobile.png';
import whitelightLogoMobile from '@/assets/companies/whitelight-logo-mobile.png';
import scurveLogoMobile from '@/assets/companies/scurve-logo-mobile.png';

import evexBg from '@/assets/companies/evex-bg.jpg';
import focusBg from '@/assets/companies/focus-bg.jpg';
import whitelightBg from '@/assets/companies/whitelight-bg.jpg';
import scurveBg from '@/assets/companies/scurve-bg.jpg';

const AnimatedCounter = ({ value, prefix, suffix }: { value: number; prefix: string; suffix: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const count = useCountUp(value, ref, 2500, prefix, suffix);
  return (
    <div ref={ref} className="text-3xl md:text-4xl lg:text-5xl font-black text-primary">
      {count}
    </div>
  );
};

interface CompanyData {
  name: string;
  key: string;
  logo: string;
  mobileLogo: string;
  image: string;
  brandColor: string;
  url: string;
}

const companiesData: CompanyData[] = [
  {
    name: 'EVEX',
    key: 'evex',
    logo: evexLogo,
    mobileLogo: evexLogoMobile,
    image: evexBg,
    brandColor: 'hsla(184, 100%, 29%, 0.35)',
    url: 'https://evex.sa/',
  },
  {
    name: 'iFocus',
    key: 'focus',
    logo: focusLogo,
    mobileLogo: focusLogoMobile,
    image: focusBg,
    brandColor: 'hsla(0, 85%, 50%, 0.3)',
    url: 'https://ifocus.sa/',
  },
  {
    name: 'WHITE LIGHT',
    key: 'whitelight',
    logo: whitelightLogo,
    mobileLogo: whitelightLogoMobile,
    image: whitelightBg,
    brandColor: 'hsla(200, 80%, 50%, 0.3)',
    url: 'https://whitesa.co/',
  },
  {
    name: 'scurve',
    key: 'scurve',
    logo: scurveLogo,
    mobileLogo: scurveLogoMobile,
    image: scurveBg,
    brandColor: 'hsla(145, 60%, 45%, 0.3)',
    url: 'https://scurve.sa/',
  },
];

const statsData = [
  { value: 15, prefix: '+', suffix: '', labelKey: 'services.stats.yearsExperience' },
  { value: 100, prefix: '+', suffix: '', labelKey: 'services.stats.eventsExecuted' },
  { value: 500, prefix: '+', suffix: 'K', labelKey: 'services.stats.visitors' },
  { value: 50, prefix: '+', suffix: '', labelKey: 'services.stats.partners' },
  { value: 20, prefix: '+', suffix: '', labelKey: 'services.stats.awards' },
];

interface EcosystemCardProps {
  company: CompanyData;
  isActive: boolean;
  onHover: () => void;
  onLeave: () => void;
}

const EcosystemCard = ({ company, isActive, onHover, onLeave }: EcosystemCardProps) => {
  const { t } = useTranslation();
  const tags = t(`services.companies.${company.key}.tags`, { returnObjects: true }) as string[];

  return (
    <div
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      className={`relative rounded-xl overflow-hidden cursor-pointer min-h-[500px] lg:min-h-[560px] transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${
        isActive ? 'flex-[3]' : 'flex-[1]'
      }`}
      style={{ willChange: 'flex' }}
    >
      <div
        className={`absolute inset-0 bg-cover bg-center transition-all duration-700 ${
          isActive ? 'grayscale-0 scale-105' : 'grayscale'
        }`}
        style={{ backgroundImage: `url(${company.image})` }}
      />

      <div
        className={`absolute inset-0 transition-opacity duration-700 ${isActive ? 'opacity-100' : 'opacity-0'}`}
        style={{ backgroundColor: company.brandColor }}
      />

      <div className={`absolute inset-0 transition-all duration-700 ${
        isActive
          ? 'bg-gradient-to-t from-black/95 via-black/60 to-black/30'
          : 'bg-gradient-to-t from-black/90 via-black/40 to-black/10'
      }`} />

      <div className="relative z-10 h-full flex flex-col justify-end p-6 lg:p-8">
        <img
          src={company.logo}
          alt={`${company.name} logo`}
          className={`h-20 lg:h-24 w-auto object-contain object-left mb-3 transition-all duration-500 ${
            isActive ? 'brightness-0 invert drop-shadow-[0_0_20px_rgba(255,255,255,0.8)] drop-shadow-[0_0_40px_rgba(255,255,255,0.4)]' : 'brightness-0 invert drop-shadow-[0_0_12px_rgba(255,255,255,0.6)]'
          }`}
        />

        <p className="text-primary text-xs lg:text-sm font-bold uppercase tracking-wider mb-2">
          {t(`services.companies.${company.key}.category`)}
        </p>

        <div
          className={`transition-all duration-500 overflow-hidden ${
            isActive ? 'max-h-60 opacity-100 translate-y-0' : 'max-h-0 opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: isActive ? '150ms' : '0ms' }}
        >
          <p className="text-foreground/80 text-sm leading-relaxed mb-4 max-w-md">
            {t(`services.companies.${company.key}.description`)}
          </p>

          <div className="flex flex-wrap gap-2 mb-5">
            {tags.map((tag) => (
              <span
                key={tag}
                className="text-xs border border-foreground/30 text-foreground/80 rounded-full px-3 py-1 uppercase tracking-wider"
              >
                {tag}
              </span>
            ))}
          </div>

          <a
            href={company.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary font-semibold text-sm uppercase tracking-wider hover:gap-3 transition-all duration-300"
          >
            {t('services.exploreDetails')} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export const Services = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [mobileActiveIndex, setMobileActiveIndex] = useState<number | null>(null);

  return (
    <section id="companies" className="py-12 lg:py-32 bg-background relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent" />
      </div>

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center gap-4 text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-tight text-foreground justify-center mb-4">
            <img src="/title-icon.png" alt="" className="w-8 h-8 md:w-10 md:h-10" />
            {t('services.label')}
          </div>
          <motion.a
            href="#"
            className="inline-flex items-center gap-2 text-primary font-semibold group"
            whileHover={{ x: 5 }}
          >
            {t('services.exploreAll')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </motion.a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="hidden md:flex gap-4 mb-20"
        >
          {companiesData.map((company, index) => (
            <EcosystemCard
              key={company.name}
              company={company}
              isActive={activeIndex === index}
              onHover={() => setActiveIndex(index)}
              onLeave={() => setActiveIndex(null)}
            />
          ))}
        </motion.div>

        <div className="md:hidden flex flex-col gap-4 mb-20">
          {companiesData.map((company, index) => {
            const isExpanded = mobileActiveIndex === index;
            const tags = t(`services.companies.${company.key}.tags`, { returnObjects: true }) as string[];
            return (
              <motion.div
                key={company.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onClick={() => setMobileActiveIndex(isExpanded ? null : index)}
                className="relative rounded-xl overflow-hidden cursor-pointer"
              >
                <div
                  className={`absolute inset-0 bg-cover bg-center transition-all duration-500 ${
                    isExpanded ? 'scale-105 grayscale-0' : 'scale-100 grayscale'
                  }`}
                  style={{ backgroundImage: `url(${company.image})` }}
                />

                <div
                  className={`absolute inset-0 transition-opacity duration-500 ${isExpanded ? 'opacity-100' : 'opacity-0'}`}
                  style={{ backgroundColor: company.brandColor }}
                />

                <div className={`absolute inset-0 transition-all duration-500 ${
                  isExpanded
                    ? 'bg-gradient-to-t from-black/95 via-black/60 to-black/30'
                    : 'bg-gradient-to-t from-black/90 via-black/40 to-black/10'
                }`} />

                <div className="relative z-10 flex flex-col justify-end p-6">
                  <div className="flex items-end justify-between mb-2">
                    <img
                      src={company.mobileLogo}
                      alt={`${company.name} logo`}
                      className={`h-28 max-w-[260px] w-auto object-contain brightness-0 invert transition-all duration-500 ${
                        isExpanded ? 'drop-shadow-[0_0_20px_rgba(255,255,255,0.8)]' : ''
                      }`}
                    />
                    <ChevronDown className={`w-5 h-5 text-foreground/60 transition-transform duration-300 ${
                      isExpanded ? 'rotate-180' : ''
                    }`} />
                  </div>

                  <p className="text-primary text-sm font-extrabold uppercase tracking-wider mb-2">
                    {t(`services.companies.${company.key}.category`)}
                  </p>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="text-foreground/80 text-sm leading-relaxed mb-4">
                          {t(`services.companies.${company.key}.description`)}
                        </p>

                        <div className="flex flex-wrap gap-2 mb-4">
                          {tags.map((tag) => (
                            <span
                              key={tag}
                              className="text-xs border border-foreground/30 text-foreground/80 rounded-full px-3 py-1 uppercase tracking-wider"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        <a
                          href={company.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-2 text-primary font-semibold text-sm uppercase tracking-wider"
                          data-testid={`link-explore-${company.key}`}
                        >
                          {t('services.exploreDetails')} <ArrowRight className="w-4 h-4" />
                        </a>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-12 lg:gap-16">
          {statsData.map((stat, index) => (
            <motion.div
              key={stat.labelKey}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 1.2, delay: 0.1 + index * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="text-center"
            >
              <AnimatedCounter value={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
              <p className="text-muted-foreground text-xs md:text-sm tracking-wide mt-4 leading-relaxed">
                {t(stat.labelKey)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
